import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import './Dashboard.css';

function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    broadcasts: 0,
    goals: 0,
    assets: 0,
    pendingLeaves: 0,
    totalEmployees: 0,
    activeEmployees: 0,
    activeProjects: 0,
    goalsInProgress: 0,
    pendingApprovals: 0,
    presentToday: 0,
    absentToday: 0,
    completedProjects: 0,
    upcomingDeadlines: 0
  });
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [departmentDistribution, setDepartmentDistribution] = useState({
    departments: [],
    employeeCounts: []
  });
  const [personalAttendance, setPersonalAttendance] = useState({
    presentDays: 0,
    absentDays: 0,
    halfDays: 0,
    lateMarks: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [weeklyAnalytics, setWeeklyAnalytics] = useState({
    days: [],
    goalsCompleted: [],
    completedProjects: [],
    newJoining: [],
    upcomingDeadlines: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const weeklyChartRef = useRef(null);
  const weeklyChartInstance = useRef(null);
  const personalAttendanceChartRef = useRef(null);
  const personalAttendanceChartInstance = useRef(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const isAdmin = user?.role?.permissions?.some(
        p => p.module === 'employees' && p.actions.includes('update')
      );

      if (isAdmin) {
        await Promise.all([
          fetchStats(),
          fetchTodayAttendance(),
          fetchDepartmentDistribution(),
          fetchWeeklyAnalytics()
        ]);
      } else {
        await Promise.all([
          fetchStats(),
          fetchTodayAttendance(),
          fetchPersonalAttendance(),
          fetchWeeklyAnalytics()
        ]);
      }
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  useEffect(() => {
    if (departmentDistribution.departments.length > 0) {
      renderChart();
    }
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [departmentDistribution]);

  useEffect(() => {
    if (weeklyAnalytics.days.length > 0) {
      renderWeeklyChart();
    }
    return () => {
      if (weeklyChartInstance.current) {
        weeklyChartInstance.current.destroy();
      }
    };
  }, [weeklyAnalytics]);

  useEffect(() => {
    const isAdmin = user?.role?.permissions?.some(
      p => p.module === 'employees' && p.actions.includes('update')
    );

    if (!isAdmin && !loading) {
      renderPersonalAttendanceChart();
    }
    return () => {
      if (personalAttendanceChartInstance.current) {
        personalAttendanceChartInstance.current.destroy();
      }
    };
  }, [personalAttendance, loading]);

  const fetchTodayAttendance = async () => {
    try {
      const response = await api.get('/attendance/today');
      if (response.data) {
        setTodayAttendance(response.data);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setTodayAttendance(null);
    }
  };

  const fetchPersonalAttendance = async () => {
    try {
      const response = await api.get('/attendance/my-stats');
      if (response.data) {
        setPersonalAttendance({
          presentDays: response.data.presentDays || 0,
          absentDays: response.data.absentDays || 0,
          halfDays: response.data.halfDays || 0,
          lateMarks: response.data.lateMarks || 0
        });
      }
    } catch (error) {
      console.error('Error fetching personal attendance:', error);
      setPersonalAttendance({
        presentDays: 0,
        absentDays: 0,
        halfDays: 0,
        lateMarks: 0
      });
    }
  };

  const fetchDepartmentDistribution = async () => {
    try {
      const response = await api.get('/employees');
      if (!response.data || response.data.length === 0) {
        setDepartmentDistribution({ departments: [], employeeCounts: [] });
        return;
      }

      const employees = response.data;

      // Dynamically group employees by their actual department
      const departmentMap = {};

      employees.forEach(emp => {
        let dept = emp.department || 'Unassigned';

        // Filter out email addresses and invalid department names
        if (dept.includes('@') || dept.trim() === '') {
          dept = 'Unassigned';
        }

        departmentMap[dept] = (departmentMap[dept] || 0) + 1;
      });

      // Sort departments by employee count (descending) and take top 8
      const sortedDepartments = Object.entries(departmentMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)  // Limit to top 8 departments
        .reduce((acc, [dept, count]) => {
          acc.departments.push(dept);
          acc.counts.push(count);
          return acc;
        }, { departments: [], counts: [] });

      setDepartmentDistribution({
        departments: sortedDepartments.departments,
        employeeCounts: sortedDepartments.counts
      });
    } catch (error) {
      console.error('Error fetching department distribution:', error);
      setDepartmentDistribution({ departments: [], employeeCounts: [] });
    }
  };

  const renderChart = () => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');

    // Destroy existing chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Load Chart.js dynamically
    if (window.Chart) {
      chartInstance.current = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels: departmentDistribution.departments,
          datasets: [
            {
              label: 'Number of Employees',
              data: departmentDistribution.employeeCounts,
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderWidth: 3,
              tension: 0.4,
              fill: true,
              pointBackgroundColor: '#3b82f6',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              pointRadius: 6,
              pointHoverRadius: 8
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 12,
              titleFont: {
                size: 14,
                weight: '600'
              },
              bodyFont: {
                size: 13
              },
              cornerRadius: 8,
              callbacks: {
                label: function (context) {
                  return 'Employees: ' + context.parsed.y;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
                font: {
                  size: 11
                }
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              }
            },
            x: {
              ticks: {
                font: {
                  size: 10,
                  weight: '500'
                },
                maxRotation: 45,
                minRotation: 45,
                autoSkip: false
              },
              grid: {
                display: false
              }
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          }
        }
      });
    }
  };

  const renderPersonalAttendanceChart = () => {
    if (!personalAttendanceChartRef.current) return;

    const ctx = personalAttendanceChartRef.current.getContext('2d');

    if (personalAttendanceChartInstance.current) {
      personalAttendanceChartInstance.current.destroy();
    }

    if (window.Chart) {
      personalAttendanceChartInstance.current = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels: ['Present Days', 'Absent Days', 'Half Days', 'Late Marks'],
          datasets: [
            {
              label: 'Number of Days',
              data: [
                personalAttendance.presentDays,
                personalAttendance.absentDays,
                personalAttendance.halfDays,
                personalAttendance.lateMarks
              ],
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderWidth: 3,
              tension: 0.4,
              fill: true,
              pointBackgroundColor: ['#10b981', '#ef4444', '#f59e0b', '#8b5cf6'],
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              pointRadius: 8,
              pointHoverRadius: 10
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 12,
              titleFont: {
                size: 14,
                weight: '600'
              },
              bodyFont: {
                size: 13
              },
              cornerRadius: 8,
              callbacks: {
                label: function (context) {
                  return 'Days: ' + context.parsed.y;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
                font: {
                  size: 11
                }
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              }
            },
            x: {
              ticks: {
                font: {
                  size: 11,
                  weight: '500'
                }
              },
              grid: {
                display: false
              }
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          }
        }
      });
    }
  };

  const fetchWeeklyAnalytics = async () => {
    try {
      const [goals, employees, projects] = await Promise.all([
        api.get('/goals').catch(() => ({ data: [] })),
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/projects').catch(() => ({ data: [] }))
      ]);

      const goalsData = Array.isArray(goals.data) ? goals.data : [];
      const employeesData = Array.isArray(employees.data) ? employees.data : [];
      const projectsData = Array.isArray(projects.data) ? projects.data : [];

      const daysData = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const goalsCompletedData = [];
      const completedProjectsData = [];
      const newJoiningData = [];
      const upcomingDeadlinesData = [];

      const today = new Date();
      const currentDay = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (currentDay === 0 ? 6 : currentDay - 1));

      for (let i = 0; i < 7; i++) {
        const dayDate = new Date(monday);
        dayDate.setDate(monday.getDate() + i);
        const dayStart = new Date(dayDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayDate);
        dayEnd.setHours(23, 59, 59, 999);

        // Count TOTAL goals completed on this day (across all documents)
        let totalCompletedGoals = 0;
        goalsData.forEach(g => {
          g.goals?.forEach(item => {
            if (item.isCompleted && item.completedAt) {
              const completedDate = new Date(item.completedAt);
              if (completedDate >= dayStart && completedDate <= dayEnd) {
                totalCompletedGoals++;
              }
            }
          });
        });

        // Count projects completed on this day
        const completedProjectsCount = projectsData.filter(p => {
          if (p.status === 'completed' && p.updatedAt) {
            const completedDate = new Date(p.updatedAt);
            return completedDate >= dayStart && completedDate <= dayEnd;
          }
          return false;
        }).length;

        // Count new joinings on this day
        const newJoins = employeesData.filter(e => {
          const joiningDate = new Date(e.joiningDate || e.createdAt);
          return joiningDate >= dayStart && joiningDate <= dayEnd;
        }).length;

        // Count projects with deadlines on THIS specific day
        const upcomingProjects = projectsData.filter(p => {
          if (p.deadline) {
            const deadline = new Date(p.deadline);
            deadline.setHours(0, 0, 0, 0);
            return deadline.getTime() === dayStart.getTime();
          }
          return false;
        }).length;

        goalsCompletedData.push(totalCompletedGoals);
        completedProjectsData.push(completedProjectsCount);
        newJoiningData.push(newJoins);
        upcomingDeadlinesData.push(upcomingProjects);
      }

      setWeeklyAnalytics({
        days: daysData,
        goalsCompleted: goalsCompletedData,
        completedProjects: completedProjectsData,
        newJoining: newJoiningData,
        upcomingDeadlines: upcomingDeadlinesData
      });
    } catch (error) {
      console.error('Error fetching weekly analytics:', error);
      setWeeklyAnalytics({
        days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        goalsCompleted: [0, 0, 0, 0, 0, 0, 0],
        completedProjects: [0, 0, 0, 0, 0, 0, 0],
        newJoining: [0, 0, 0, 0, 0, 0, 0],
        upcomingDeadlines: [0, 0, 0, 0, 0, 0, 0]
      });
    }
  };

  const renderWeeklyChart = () => {
    if (!weeklyChartRef.current) return;

    const ctx = weeklyChartRef.current.getContext('2d');

    if (weeklyChartInstance.current) {
      weeklyChartInstance.current.destroy();
    }

    if (window.Chart) {
      weeklyChartInstance.current = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels: weeklyAnalytics.days,
          datasets: [
            {
              label: 'Goals Completed',
              data: weeklyAnalytics.goalsCompleted,
              backgroundColor: '#3b82f6',
              borderRadius: 4,
              barThickness: 15
            },
            {
              label: 'Completed Project',
              data: weeklyAnalytics.completedProjects,
              backgroundColor: '#a855f7',
              borderRadius: 4,
              barThickness: 15
            },
            {
              label: 'New Joinings',
              data: weeklyAnalytics.newJoining,
              backgroundColor: '#f59e0b',
              borderRadius: 4,
              barThickness: 15
            },
            {
              label: 'Upcoming Deadlines',
              data: weeklyAnalytics.upcomingDeadlines,
              backgroundColor: '#10b981',
              borderRadius: 4,
              barThickness: 15
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                padding: 10,
                boxWidth: 12,
                font: {
                  size: 10,
                  weight: '600'
                },
                usePointStyle: true
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 10,
              titleFont: {
                size: 12,
                weight: '600'
              },
              bodyFont: {
                size: 11
              },
              cornerRadius: 6
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
                font: {
                  size: 10
                }
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              }
            },
            x: {
              ticks: {
                font: {
                  size: 10
                }
              },
              grid: {
                display: false
              }
            }
          }
        }
      });
    }
  };

  const fetchStats = async () => {
    try {
      const [broadcasts, goals, assets, leaves, employees, projects, requests, attendanceStats] = await Promise.all([
        api.get('/broadcasts').catch(() => ({ data: [] })),
        api.get('/goals').catch(() => ({ data: [] })),
        api.get('/assets').catch(() => ({ data: [] })),
        api.get('/leaves').catch(() => ({ data: [] })),
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/projects').catch(() => ({ data: [] })),
        api.get('/requests').catch(() => ({ data: [] })),
        api.get('/attendance/today-stats').catch(() => ({ data: { presentToday: 0, absentToday: 0 } }))
      ]);

      // Calculate stats from actual API data
      const activeEmployees = employees.data.filter(emp => emp.isActive === true || emp.status === 'active');

      // Check if user is admin
      const isAdmin = user?.role?.permissions?.some(
        p => p.module === 'employees' && p.actions.includes('update')
      );

      // Filter active projects based on user role
      let activeProjects;
      if (isAdmin) {
        // Admin sees all active projects
        activeProjects = projects.data.filter(proj => proj.status === 'active' || proj.status === 'in-progress');
      } else {
        // Employee sees only their assigned projects
        activeProjects = projects.data.filter(proj => {
          const isActive = proj.status === 'active' || proj.status === 'in-progress';
          const isAssigned = proj.assignedTo?.some(emp => emp._id === user._id || emp === user._id) ||
            proj.teamMembers?.some(emp => emp._id === user._id || emp === user._id) ||
            proj.createdBy?._id === user._id || proj.createdBy === user._id;
          return isActive && isAssigned;
        });
      }

      const goalsInProgress = goals.data.filter(g => {
        const completedGoals = g.goals?.filter(item => item.isCompleted).length || 0;
        const totalGoals = g.goals?.length || 0;
        return completedGoals < totalGoals && totalGoals > 0;
      });
      const pendingApprovals = [
        ...leaves.data.filter(l => l.status === 'pending'),
        ...requests.data.filter(r => r.status === 'pending')
      ];

      setStats({
        broadcasts: broadcasts.data.length || 0,
        goals: goals.data.length || 0,
        assets: assets.data.length || 0,
        pendingLeaves: leaves.data.filter(l => l.status === 'pending').length || 0,
        totalEmployees: employees.data.length || 0,
        activeEmployees: activeEmployees.length || 0,
        activeProjects: activeProjects.length || 0,
        goalsInProgress: goalsInProgress.length || 0,
        pendingApprovals: pendingApprovals.length || 0,
        presentToday: attendanceStats.data.presentToday || 0,
        absentToday: attendanceStats.data.absentToday || 0,
        completedProjects: projects.data.filter(p => p.status === 'completed').length || 0,
        upcomingDeadlines: projects.data.filter(p => {
          if (p.deadline && p.status !== 'completed') {
            const d = new Date(p.deadline);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const endOfDay = new Date(today);
            endOfDay.setHours(23, 59, 59, 999);
            return d >= today && d <= endOfDay;
          }
          return false;
        }).length || 0
      });

      // Get recent 5 leaves sorted by date
      const sortedLeaves = [...leaves.data].sort((a, b) => new Date(b.createdAt || b.startDate) - new Date(a.createdAt || a.startDate));
      setRecentLeaves(sortedLeaves.slice(0, 5));

      // Compile recent HR activities from API data
      const activities = [];

      // Add recent broadcasts (last 3)
      const sortedBroadcasts = [...broadcasts.data].sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date));
      sortedBroadcasts.slice(0, 3).forEach(b => {
        activities.push({
          type: 'broadcast',
          icon: '📢',
          title: b.title || 'New Broadcast',
          description: (b.message || b.description || '').substring(0, 60) + (b.message?.length > 60 ? '...' : ''),
          timestamp: b.createdAt || b.date || new Date(),
          color: '#3b82f6'
        });
      });

      // Add recent goals (last 3)
      const sortedGoals = [...goals.data].sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date));
      sortedGoals.slice(0, 3).forEach(g => {
        activities.push({
          type: 'goal',
          icon: '🎯',
          title: `Goal by ${g.employee?.firstName || g.employeeName || 'Employee'}`,
          description: g.project?.name || g.projectName || 'Project goal created',
          timestamp: g.createdAt || g.date || new Date(),
          color: '#10b981'
        });
      });

      // Add recent requests (last 3)
      const sortedRequests = [...requests.data].sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date));
      sortedRequests.slice(0, 3).forEach(r => {
        activities.push({
          type: 'request',
          icon: '📋',
          title: `Request - ${r.requestType || r.type || 'General'}`,
          description: `Status: ${r.status}`,
          timestamp: r.createdAt || r.date || new Date(),
          color: '#f59e0b'
        });
      });

      // Sort by timestamp and take latest 6
      activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setRecentActivities(activities.slice(0, 6));
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default values on error
      setStats({
        broadcasts: 0,
        goals: 0,
        assets: 0,
        pendingLeaves: 0,
        totalEmployees: 0,
        activeEmployees: 0,
        activeProjects: 0,
        goalsInProgress: 0,
        pendingApprovals: 0,
        presentToday: 0,
        absentToday: 0
      });
    }
  };

  return (
    <div className="container" style={{ padding: '15px 20px', maxWidth: 'calc(100vw - 280px)', overflowX: 'auto' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <h1 className="dashboard-welcome">Welcome, {user?.firstName || 'User'}!</h1>
        <button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          style={{
            padding: 'clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: refreshing || loading ? 'not-allowed' : 'pointer',
            opacity: refreshing || loading ? 0.6 : 1,
            fontWeight: '600',
            fontSize: 'clamp(11px, 2.5vw, 13px)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          <span style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}>{refreshing ? '⏳' : '🔄'}</span>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#64748b',
          fontSize: '16px'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>⏳</div>
          Loading dashboard data...
        </div>
      )}

      {error && (
        <div style={{
          padding: '15px',
          backgroundColor: '#fee2e2',
          color: '#dc2626',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #fca5a5'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && (
        <>

          <div className="card attendance-card" style={{ padding: '10px 15px', marginBottom: '15px' }}>
            <h3 style={{ fontSize: '15px', margin: '0 0 8px 0', color: 'white' }}>📊 Today's Attendance</h3>
            <div className="attendance-grid" style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '15px',
              justifyContent: 'space-between',
              marginBottom: '5px'
            }}>
              <div className="attendance-stat" style={{ flex: '1', minWidth: '100px', padding: '5px', textAlign: 'center' }}>
                <p className="attendance-stat-label" style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '2px' }}>Status</p>
                <p className="attendance-stat-value" style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0', color: 'white' }}>
                  {todayAttendance ? ((todayAttendance.goalsCompleted || 0) >= 3 ? '✓ Present' : '🔓 Logged In') : '⚪ Not Logged In'}
                </p>
                <small className="attendance-stat-sub" style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  {(todayAttendance?.goalsCompleted || 0)}/3 goals
                </small>
              </div>
              <div className="attendance-stat" style={{ flex: '1', minWidth: '80px', padding: '5px', textAlign: 'center' }}>
                <p className="attendance-stat-label" style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '2px' }}>Login Time</p>
                <p className="attendance-stat-value" style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0', color: 'white' }}>
                  {todayAttendance?.loginTime
                    ? new Date(todayAttendance.loginTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                    : '--:--'}
                </p>
              </div>
              <div className="attendance-stat" style={{ flex: '1', minWidth: '80px', padding: '5px', textAlign: 'center' }}>
                <p className="attendance-stat-label" style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '2px' }}>Working Hours</p>
                <p className="attendance-stat-value" style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0', color: 'white' }}>
                  {todayAttendance?.loginTime
                    ? `${Math.round((new Date() - new Date(todayAttendance.loginTime)) / (1000 * 60 * 60) * 100) / 100} hrs`
                    : '0 hrs'}
                </p>
              </div>
              <div className="attendance-stat" style={{ flex: '1', minWidth: '80px', padding: '5px', textAlign: 'center' }}>
                <p className="attendance-stat-label" style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '2px' }}>Goals Created</p>
                <p className="attendance-stat-value" style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0', color: 'white' }}>{todayAttendance?.goalsCreated || 0}</p>
              </div>
              <div className="attendance-stat" style={{ flex: '1', minWidth: '80px', padding: '5px', textAlign: 'center' }}>
                <p className="attendance-stat-label" style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '2px' }}>Goals Completed</p>
                <p className="attendance-stat-value" style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0', color: 'white' }}>{todayAttendance?.goalsCompleted || 0}</p>
              </div>
            </div>
            {todayAttendance && (todayAttendance.goalsCompleted || 0) < 3 && (
              <div className="attendance-warning" style={{
                marginTop: '5px',
                padding: '6px 10px',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: '#fffbeb',
                color: '#b45309',
                borderRadius: '6px'
              }}>
                <span>⚠️</span>
                <p style={{ margin: 0 }}>
                  Complete {3 - (todayAttendance.goalsCompleted || 0)} more goal(s) to mark present
                </p>
              </div>
            )}
            {!todayAttendance && (
              <div className="attendance-warning" style={{
                marginTop: '5px',
                padding: '6px 10px',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: '#eff6ff',
                color: '#1d4ed8',
                borderRadius: '6px'
              }}>
                <span>💡</span>
                <p style={{ margin: 0 }}>
                  Create your first goal to start today's session
                </p>
              </div>
            )}
          </div>

          {/* Statistics Cards - Responsive Layout */}
          <div className="stats-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '12px',
            marginBottom: '20px',
          }}>
            {user?.role?.permissions?.some(p => p.module === 'employees' && p.actions.includes('update')) && (
              <>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  padding: '12px',
                  minHeight: '80px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ fontSize: '11px', margin: '0 0 5px 0', fontWeight: '600', color: 'white' }}>✅ Present Today</h3>
                  <p style={{ fontSize: '24px', margin: '0', fontWeight: '700', color: 'white' }}>{stats.presentToday || 0}</p>
                </div>

                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  padding: '12px',
                  minHeight: '80px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ fontSize: '11px', margin: '0 0 5px 0', fontWeight: '600', color: 'white' }}>❌ Absent Today</h3>
                  <p style={{ fontSize: '24px', margin: '0', fontWeight: '700', color: 'white' }}>{stats.absentToday || 0}</p>
                </div>

                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                  padding: '12px',
                  minHeight: '80px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ fontSize: '11px', margin: '0 0 5px 0', fontWeight: '600', color: 'white' }}>🏆 Completed Projects</h3>
                  <p style={{ fontSize: '24px', margin: '0', fontWeight: '700', color: 'white' }}>{stats.completedProjects || 0}</p>
                </div>
              </>
            )}

            <div className="stat-card" style={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              padding: '12px',
              minHeight: '80px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '11px', margin: '0 0 5px 0', fontWeight: '600', color: 'white' }}>📁 Active Projects</h3>
              <p style={{ fontSize: '24px', margin: '0', fontWeight: '700', color: 'white' }}>{stats.activeProjects || 0}</p>
            </div>

            <div className="stat-card" style={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              padding: '12px',
              minHeight: '80px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '11px', margin: '0 0 5px 0', fontWeight: '600', color: 'white' }}>⏰ Upcoming Deadlines</h3>
              <p style={{ fontSize: '24px', margin: '0', fontWeight: '700', color: 'white' }}>{stats.upcomingDeadlines || 0}</p>
            </div>

            <div className="stat-card" style={{
              background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
              padding: '12px',
              minHeight: '80px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '11px', margin: '0 0 5px 0', fontWeight: '600', color: 'white' }}>🎯 My Goals</h3>
              <p style={{ fontSize: '24px', margin: '0', fontWeight: '700', color: 'white' }}>{stats.goals || 0}</p>
            </div>

            <div className="stat-card" style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              padding: '12px',
              minHeight: '80px',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '11px', margin: '0 0 5px 0', fontWeight: '600', color: 'white' }}>� Broadcasts</h3>
              <p style={{ fontSize: '24px', margin: '0', fontWeight: '700', color: 'white' }}>{stats.broadcasts || 0}</p>
            </div>

            {!user?.role?.permissions?.some(p => p.module === 'employees' && p.actions.includes('update')) && (
              <>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)',
                  padding: '12px',
                  minHeight: '80px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ fontSize: '11px', margin: '0 0 5px 0', fontWeight: '600', color: 'white' }}>⏳ Pending Approvals</h3>
                  <p style={{ fontSize: '24px', margin: '0', fontWeight: '700', color: 'white' }}>{stats.pendingApprovals || 0}</p>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                  padding: '12px',
                  minHeight: '80px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ fontSize: '11px', margin: '0 0 5px 0', fontWeight: '600', color: 'white' }}>🏖️ Pending Leaves</h3>
                  <p style={{ fontSize: '24px', margin: '0', fontWeight: '700', color: 'white' }}>{stats.pendingLeaves || 0}</p>
                </div>
              </>
            )}
          </div>



          {/* Department Distribution & Recent HR Activities */}
          <div className="card" style={{ padding: '20px', marginBottom: '20px', overflow: 'hidden' }}>
            <div className="dashboard-charts-section">
              {/* Department Distribution Chart OR Personal Attendance Stats */}
              <div>
                {user?.role?.permissions?.some(p => p.module === 'employees' && p.actions.includes('update')) ? (
                  <>
                    <h2 style={{ fontSize: '18px', marginBottom: '8px' }}>🏛️ Department-wise Employee Distribution</h2>
                    <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '15px' }}>
                      Employee count per department
                    </p>
                    <div className="chart-wrapper">
                      <canvas ref={chartRef}></canvas>
                    </div>
                    {departmentDistribution.departments.length === 0 && (
                      <p style={{ textAlign: 'center', color: '#64748b', padding: '30px 0' }}>
                        Loading department data...
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <h2 style={{ fontSize: '18px', marginBottom: '8px' }}>📊 My Attendance Statistics</h2>
                    <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '15px' }}>
                      Your monthly attendance overview
                    </p>
                    <div className="chart-wrapper">
                      <canvas ref={personalAttendanceChartRef}></canvas>
                    </div>
                  </>
                )}
              </div>

              {/* Recent HR Activities */}
              <div className="activities-sidebar">
                <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>📌 RECENT HR ACTIVITIES</h2>
                <div style={{
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  maxHeight: '320px',
                  paddingRight: '4px'
                }}>
                  {recentActivities.length > 0 ? (
                    recentActivities.map((activity, index) => (
                      <div key={index} style={{
                        padding: '12px',
                        marginBottom: '10px',
                        borderRadius: '10px',
                        backgroundColor: '#f9fafb',
                        borderLeft: `4px solid ${activity.color}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                          <span style={{ fontSize: '20px', flexShrink: 0 }}>{activity.icon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 style={{
                              margin: '0 0 4px 0',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#1f2937',
                              wordBreak: 'break-word'
                            }}>
                              {activity.title}
                            </h4>
                            <p style={{
                              margin: '0 0 4px 0',
                              fontSize: '12px',
                              color: '#6b7280',
                              lineHeight: '1.4',
                              wordBreak: 'break-word'
                            }}>
                              {activity.description}
                            </p>
                            <small style={{ fontSize: '11px', color: '#9ca3af' }}>
                              {new Date(activity.timestamp).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '30px 0' }}>
                      No recent activities
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Analytics & Recent Leave Applications in same row */}
          <div className="dashboard-bottom-grid">
            {/* Weekly Analytics Bar Chart */}
            <div className="card" style={{ padding: '20px', overflow: 'hidden', minWidth: 0, maxWidth: '100%' }}>
              <h2 style={{ fontSize: '18px', marginBottom: '8px' }}>📊 Weekly Analytics</h2>
              <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '12px' }}>
                Day-wise analysis of goals, joinings & upcoming project
              </p>
              <div className="chart-wrapper" style={{ height: '260px' }}>
                <canvas ref={weeklyChartRef}></canvas>
              </div>
              {weeklyAnalytics.days.length === 0 && (
                <p style={{ textAlign: 'center', color: '#64748b', padding: '20px 0' }}>
                  Loading weekly data...
                </p>
              )}
            </div>

            {/* Recent Leave Applications */}
            <div className="card" style={{ padding: '20px', overflow: 'hidden', minWidth: 0, maxWidth: '100%' }}>
              <h2 style={{ fontSize: '18px', marginBottom: '12px' }}>📝 Recent Leave Applications</h2>
              <div style={{
                overflowY: 'auto',
                overflowX: 'auto',
                maxHeight: '280px'
              }}>
                {recentLeaves.length > 0 ? (
                  <table className="table" style={{ minWidth: '400px', fontSize: 'clamp(10px, 1.8vw, 13px)' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: 'clamp(6px, 1.5vw, 10px)', whiteSpace: 'nowrap' }}>Employee</th>
                        <th style={{ padding: 'clamp(6px, 1.5vw, 10px)', whiteSpace: 'nowrap' }}>Leave Type</th>
                        <th style={{ padding: 'clamp(6px, 1.5vw, 10px)', whiteSpace: 'nowrap' }}>Start Date</th>
                        <th style={{ padding: 'clamp(6px, 1.5vw, 10px)', whiteSpace: 'nowrap' }}>End Date</th>
                        <th style={{ padding: 'clamp(8px, 2vw, 12px)', whiteSpace: 'nowrap' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentLeaves.map((leave) => (
                        <tr key={leave._id}>
                          <td style={{ padding: 'clamp(8px, 2vw, 12px)', whiteSpace: 'nowrap' }}>{leave.employee?.firstName || 'N/A'} {leave.employee?.lastName || ''}</td>
                          <td style={{ padding: 'clamp(8px, 2vw, 12px)', whiteSpace: 'nowrap' }}>{leave.leaveType || 'N/A'}</td>
                          <td style={{ padding: 'clamp(8px, 2vw, 12px)', whiteSpace: 'nowrap' }}>{leave.startDate ? new Date(leave.startDate).toLocaleDateString() : 'N/A'}</td>
                          <td style={{ padding: 'clamp(8px, 2vw, 12px)', whiteSpace: 'nowrap' }}>{leave.endDate ? new Date(leave.endDate).toLocaleDateString() : 'N/A'}</td>
                          <td style={{ padding: 'clamp(8px, 2vw, 12px)' }}>
                            <span className={`badge ${leave.status === 'approved' ? 'badge-success' :
                              leave.status === 'rejected' ? 'badge-danger' :
                                'badge-warning'
                              }`} style={{ fontSize: 'clamp(10px, 2vw, 12px)', padding: 'clamp(3px, 1vw, 5px) clamp(6px, 1.5vw, 10px)' }}>
                              {leave.status || 'pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ marginTop: '15px', color: '#64748b', fontSize: 'clamp(11px, 2.5vw, 14px)' }}>No leave applications yet</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;